import { useQuery } from "@tanstack/react-query";
import { PlayerCardReview, PlayerCardWidget, Review, ReviewServices } from "@/services/bedrock";
import { ReviewWidgetData } from "../reviewWidgetPreview";
import { useReputationManagementAvailable } from "../useReputationManagementAvailable";
import { REVIEW_RATING_THRESHOLDS } from "../utils";
import { PlayerCardsContextData } from "./PlayerCardsContext";
import { PreviewContextData } from "./PreviewContext";
import { createInitialRecordContextData, createRecordContextData, RecordContextData } from "./RecordContext";
import { UserContextData } from "./UserContext";

export type ReviewContextData = RecordContextData<Review> & Omit<ReviewWidgetData, "cachedRecords">;

export const InitialReviewContextData: ReviewContextData = {
  ...createInitialRecordContextData<Review>(),
  recordTypeLabel: "Review",
  featuredReviews: undefined,
  snapshot: undefined,
};

const REVIEWS_PER_PAGE = 20;

const fetchPage = (playerId: number, page: number, perPage: number, search?: string) =>
  ReviewServices.getReviews({
    playerId,
    search,
    page,
    perPage,
    status: "published",
    active: true,
    approvedForSocial: true,
    sort: "effective_date",
    order: "desc",
  });

const getRecordFromWidget = (widget: PlayerCardWidget): Review | null =>
  widget.owner?.type === "Review" ? widget.owner.review ?? null : null;

const isRecordNewer = (record: Review, otherRecord?: Review) =>
  !otherRecord || (record.publishedAt ?? "") > (otherRecord.publishedAt ?? "");

export const ReviewContext = (
  PlayerCards: PlayerCardsContextData,
  User: UserContextData,
  Preview: PreviewContextData
): ReviewContextData => {
  const records = createRecordContextData<Review>(
    PlayerCards,
    User,
    "Review",
    fetchPage,
    getRecordFromWidget,
    isRecordNewer,
    REVIEWS_PER_PAGE
  );
  const playerId = User.playerId || PlayerCards.selectedPlayerCard?.playerId;
  const reputationManagementAvailable = useReputationManagementAvailable(playerId);
  const displayTypes = (Preview.playerCard?.widgets ?? [])
    .filter(widget => widget.owner.type === "Review")
    .map(widget => (widget.owner as PlayerCardReview).displayType);
  const canLoad = !!playerId && reputationManagementAvailable === true;

  const { data: featuredReviews } = useQuery({
    queryKey: ["playerCardFeaturedReviews", playerId],
    queryFn: () =>
      ReviewServices.getReviews({
        playerId,
        featured: true,
        postable: true,
        approvedForSocial: true,
        status: "published",
        sort: "ai_shareability_score",
        order: "desc",
        perPage: 100,
      }).then(page => page.content),
    enabled: canLoad && displayTypes.includes("FEATURED_REVIEWS"),
  });

  const { data: snapshot } = useQuery({
    queryKey: ["playerCardReviewSnapshot", playerId],
    queryFn: () =>
      ReviewServices.getReviewSnapshots({ playerId, periodType: "all_time", entityScope: "players" }).then(
        page => page.content[0] ?? null
      ),
    enabled: canLoad && displayTypes.some(displayType => displayType in REVIEW_RATING_THRESHOLDS),
  });

  return { ...records, featuredReviews, snapshot };
};
